import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Grid, Box, Divider, Card, CardContent } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import { fetchPlanByUUID, deletePlan } from '../../../services/plans';
import PlanViewer from './PlanViewer';
import BulkAssignDialog from './BulkAssignDialog';

const PlanDetails = () => {
    const { uuid } = useParams();
    const theme = useTheme();
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);

    const [openAssignDialog, setOpenAssignDialog] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                const result = await fetchPlanByUUID(uuid);
                setPlan(result);
            } catch (err) {
                console.error('Erro ao procurar plano:', err);
            }
        };
        fetch();
    }, [uuid]);

    if (!plan) return <Typography>{t('plansDetails.loading')}</Typography>;

    return (
        <Card sx={{ m: 2 }}>
            <CardContent>
                <Box display="flex" gap={2}>
                    <Button variant="contained" onClick={() => setOpenAssignDialog(true)}>
                        {t('plans.assign')}
                    </Button>
                </Box>
                <BulkAssignDialog
                    open={openAssignDialog}
                    onClose={() => setOpenAssignDialog(false)}
                    planUUIDs={uuid}
                    planDurationWeeks={plan.data.durationWeeks}
                    questionnaires={plan.data.questionnaires}
                    initialQuestionnaires={plan.data.initialQuestionnaires}
                    learningContents={plan.data.learningContents}
                    initialLearningContents={plan.data.initialLearningContents}
                />

                <Divider sx={{ mt: 2 }} />

                <Box sx={{ m: 3 }}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: 3,
                            p: 3,
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Grid item xs={12}>
                            <Typography
                                variant="h1"
                                align="center"
                                gutterBottom
                                sx={{ color: theme.palette.primary.dark }}
                            >
                                {plan.name}
                            </Typography>
                            <Typography
                                variant="body1"
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '18px' }}
                            >
                                {plan.description}
                            </Typography>
                            <Typography
                                variant="body1"
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '14px' }}
                            >
                                {t('plansDetails.duration', {
                                    weeks: plan.data.durationWeeks
                                })}
                            </Typography>
                            <Typography
                                variant="body1"
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '14px' }}
                            >
                                {t('plansDetails.created_on', {
                                    date: new Date(plan.created).toLocaleDateString()
                                })}
                            </Typography>
                        </Grid>

                        <PlanViewer
                            sessions={plan.data.sessions}
                            questionnaires={plan.data.questionnaires}
                            initialQuestionnaires={plan.data.initialQuestionnaires}
                            learningContents={plan.data.learningContents}
                            initialLearningContents={plan.data.initialLearningContents}
                        />

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={4}
                        >
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={async () => {
                                    if (
                                        window.confirm(t('plansDetails.delete_confirm'))
                                    ) {
                                        try {
                                            await deletePlan(uuid);
                                            navigate('/planos');
                                        } catch (error) {
                                            console.error(
                                                t('plansDetails.delete_error'),
                                                error
                                            );
                                            alert(t('plansDetails.delete_error'));
                                        }
                                    }
                                }}
                            >
                                {t('plansDetails.delete_plan')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate(`/planos/duplicar/${uuid}`)}
                            >
                                {t('plansDetails.duplicate_plan')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/planos')}
                            >
                                {t('plansDetails.back')}
                            </Button>
                        </Box>
                    </Card>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PlanDetails;
